import { useEffect, useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X, Copy, Check } from 'lucide-react';

let _id = 0;
const listeners = new Set();
const queue = [];

function notify() {
  for (const l of listeners) l(queue.slice());
}

function pushToast(t) {
  const id = ++_id;
  const item = { id, kind: t.kind || 'info', title: t.title || '', message: t.message || '', link: t.link || null, duration: t.duration ?? (t.kind === 'error' ? 6500 : 4000) };
  queue.push(item);
  notify();
  if (item.duration > 0) {
    setTimeout(() => dismiss(id), item.duration);
  }
  return id;
}

function dismiss(id) {
  const idx = queue.findIndex(t => t.id === id);
  if (idx >= 0) {
    queue.splice(idx, 1);
    notify();
  }
}

export const toast = {
  success: (msg, opts = {}) => pushToast({ kind: 'success', message: msg, ...opts }),
  error:   (msg, opts = {}) => pushToast({ kind: 'error',   message: msg, ...opts }),
  warn:    (msg, opts = {}) => pushToast({ kind: 'warn',    message: msg, ...opts }),
  info:    (msg, opts = {}) => pushToast({ kind: 'info',    message: msg, ...opts }),
  dismiss,
};

const STYLES = {
  success: { icon: CheckCircle, color: '#00ff88', bg: 'rgba(0,255,136,.10)', border: 'rgba(0,255,136,.32)' },
  error:   { icon: XCircle,     color: '#ff3d6b', bg: 'rgba(255,61,107,.10)', border: 'rgba(255,61,107,.32)' },
  warn:    { icon: AlertTriangle, color: '#ffb700', bg: 'rgba(255,183,0,.10)', border: 'rgba(255,183,0,.32)' },
  info:    { icon: Info,        color: '#00d4ff', bg: 'rgba(0,212,255,.10)', border: 'rgba(0,212,255,.32)' },
};

function ToastItem({ item }) {
  const s = STYLES[item.kind] || STYLES.info;
  const Icon = s.icon;
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <div style={{
      background: 'var(--c-bg2,#0b1622)',
      border: `1px solid ${s.border}`,
      borderLeft: `4px solid ${s.color}`,
      borderRadius: 12,
      padding: '12px 14px',
      minWidth: 320,
      maxWidth: 420,
      boxShadow: '0 12px 40px rgba(0,0,0,.45), 0 0 24px ' + s.bg,
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      animation: 'dkt-toast-in .22s cubic-bezier(.2,.9,.3,1.2)',
      pointerEvents: 'auto',
      fontFamily: 'var(--font-body,Sora,sans-serif)',
    }}>
      <div style={{ flexShrink: 0, color: s.color, marginTop: 1 }}>
        <Icon size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.title ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: 3 }}>{item.title}</div>
        ) : null}
        <div style={{ fontSize: 12.5, color: 'var(--c-text,#c8dff0)', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {item.message}
        </div>
        {item.link ? (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={onCopy} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11, fontWeight: 600,
              padding: '5px 10px', borderRadius: 6,
              background: copied ? 'rgba(0,255,136,.14)' : 'var(--c-surface2,#112538)',
              border: `1px solid ${copied ? 'rgba(0,255,136,.32)' : 'var(--c-border,#1a3550)'}`,
              color: copied ? '#00ff88' : 'var(--c-text,#c8dff0)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {copied ? <><Check size={11}/> Copied</> : <><Copy size={11}/> Copy link</>}
            </button>
            <a href={item.link} target="_blank" rel="noopener noreferrer" style={{
              fontSize: 11, color: s.color, textDecoration: 'none',
              fontFamily: 'var(--font-mono,monospace)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            }}>{item.link}</a>
          </div>
        ) : null}
      </div>
      <button onClick={() => dismiss(item.id)} style={{
        background: 'transparent', border: 'none', color: 'var(--c-text3,#45688a)',
        cursor: 'pointer', padding: 2, borderRadius: 4, flexShrink: 0,
      }} aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  );
}

export function ToastViewport() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const fn = (snap) => setItems(snap);
    listeners.add(fn);
    fn(queue.slice());
    return () => listeners.delete(fn);
  }, []);
  return (
    <>
      {/* F-N7 (audit 2026-05-22): keyframe moved to crm-app/styles/dkt.css
          so the SPA's CSP can drop `style-src 'unsafe-inline'`. */}
      <div style={{
        position: 'fixed',
        top: 16,
        right: 16,
        // Above every modal/overlay (which sit at zIndex 9999) so a toast fired
        // while a dialog is open — e.g. the "ya se envió" notice on the signature
        // send modal — is never hidden behind it (Lissette 2026-06-17). E-014.
        zIndex: 2147483000,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        pointerEvents: 'none',
        maxHeight: 'calc(100vh - 32px)',
        overflow: 'hidden',
      }}>
        {items.map(it => <ToastItem key={it.id} item={it} />)}
      </div>
    </>
  );
}
